import { COURIER_SCREEN_X_RATIO } from '../../../../../domain/catalog/flightPhysics';
import { RUNNER_LANE_MAX } from '../../../../../domain/runner/runnerConstants';
import type { RunnerLevel, RunnerPlayer } from '../../../../../domain/runner/runnerTypes';
import {
  PC_COURIER_SCREEN_X_RATIO,
  PC_RUNNER_DISPLAY_ZOOM,
} from '../gameplayConstants';

export type LevelCanvasLayout = {
  canvasLeftPx: number;
  canvasTopPx: number;
  canvasWidthPx: number;
  canvasHeightPx: number;
  contentOffsetPx: number;
  displayScale: number;
};

export type PlayfieldConfig = {
  widthPx: number;
  heightPx: number;
  courierScreenX: number;
};

export function createPlayfieldConfig(
  widthPx: number,
  heightPx: number,
  desktopHost = false,
): PlayfieldConfig {
  const courierRatio = desktopHost ? PC_COURIER_SCREEN_X_RATIO : COURIER_SCREEN_X_RATIO;
  return {
    widthPx,
    heightPx,
    courierScreenX: widthPx * courierRatio,
  };
}

function computeDisplayScale(
  playfieldHeightPx: number,
  viewBoxHeight: number,
  desktopHost: boolean,
): number {
  if (viewBoxHeight <= 0 || playfieldHeightPx <= 0) {
    return 1;
  }
  const baseDisplayScale = playfieldHeightPx / viewBoxHeight;
  return desktopHost ? baseDisplayScale * PC_RUNNER_DISPLAY_ZOOM : baseDisplayScale;
}

function computeCanvasTopPx(
  playfieldHeightPx: number,
  viewBoxHeight: number,
  displayScale: number,
  desktopHost: boolean,
): number {
  if (!desktopHost) {
    return 0;
  }
  return Math.max(0, (playfieldHeightPx - viewBoxHeight * displayScale) / 2);
}

/**
 * Минимальный lead (в единицах route s), чтобы левый край мира
 * не был правее 0 во вьюпорте при текущем worldS.
 */
export function computeStartCameraLeadS(
  level: RunnerLevel,
  playfield: PlayfieldConfig,
  player: RunnerPlayer,
  desktopHost = false,
): number {
  const displayScale = computeDisplayScale(
    playfield.heightPx,
    level.viewBox.height,
    desktopHost,
  );
  if (displayScale <= 0) {
    return 0;
  }
  // worldLeft = courierScreenX - (worldS + lead + spawnSvgX) * scale
  // worldLeft <= 0  ⇒  lead >= courierScreenX / scale - worldS - spawnSvgX
  const lead =
    playfield.courierScreenX / displayScale - player.worldS - level.spawnSvgX;
  return Math.max(0, lead);
}

export function computeRunnerLevelLayout(
  level: RunnerLevel,
  playfield: PlayfieldConfig,
  player: RunnerPlayer,
  cameraLeadS = 0,
  desktopHost = false,
): LevelCanvasLayout {
  const displayScale = computeDisplayScale(
    playfield.heightPx,
    level.viewBox.height,
    desktopHost,
  );
  const canvasTopPx = computeCanvasTopPx(
    playfield.heightPx,
    level.viewBox.height,
    displayScale,
    desktopHost,
  );
  return {
    canvasLeftPx:
      playfield.courierScreenX - (player.worldS + cameraLeadS) * displayScale,
    canvasTopPx,
    canvasWidthPx: level.viewBox.width * displayScale,
    canvasHeightPx: playfield.heightPx,
    contentOffsetPx: -level.spawnSvgX * displayScale,
    displayScale,
  };
}

export function computeRunnerFallbackLayout(
  level: RunnerLevel,
  playfield: PlayfieldConfig,
  desktopHost = false,
): LevelCanvasLayout {
  const displayScale = computeDisplayScale(
    playfield.heightPx,
    level.viewBox.height,
    desktopHost,
  );
  const canvasTopPx = computeCanvasTopPx(
    playfield.heightPx,
    level.viewBox.height,
    displayScale,
    desktopHost,
  );
  return {
    canvasLeftPx: playfield.courierScreenX,
    canvasTopPx,
    canvasWidthPx: level.viewBox.width * displayScale,
    canvasHeightPx: playfield.heightPx,
    contentOffsetPx: -level.spawnSvgX * displayScale,
    displayScale,
  };
}

export type PlayerScreenPosition = {
  leftPx: number;
  topPx: number;
};

export function projectRunnerPlayerToScreen(
  level: RunnerLevel,
  player: RunnerPlayer,
  layout: LevelCanvasLayout,
): PlayerScreenPosition {
  const svgX = level.startPointSvgX + player.worldS;
  const svgY = (RUNNER_LANE_MAX - player.lane) * level.viewBox.height;
  return {
    leftPx: layout.canvasLeftPx + svgX * layout.displayScale + layout.contentOffsetPx,
    topPx: layout.canvasTopPx + svgY * layout.displayScale,
  };
}

export function projectSvgPointToScreen(
  svgX: number,
  svgY: number,
  layout: LevelCanvasLayout,
): PlayerScreenPosition {
  return {
    leftPx: layout.canvasLeftPx + svgX * layout.displayScale + layout.contentOffsetPx,
    topPx: layout.canvasTopPx + svgY * layout.displayScale,
  };
}

/** Сдвиг мира в координатах playfield (камера + spawn offset). */
export function worldTranslateXPx(layout: LevelCanvasLayout): number {
  return layout.canvasLeftPx + layout.contentOffsetPx;
}

/**
 * Camera X from continuous worldS (same as worldTranslateXPx of a fresh layout).
 * Used by RAF so React layout can stay on a frozen pose snapshot.
 */
export function computeWorldTranslateXPx(
  worldS: number,
  cameraLeadS: number,
  courierScreenX: number,
  displayScale: number,
  spawnSvgX: number,
): number {
  return (
    courierScreenX -
    (worldS + cameraLeadS) * displayScale -
    spawnSvgX * displayScale
  );
}

/** Координаты внутри world-camera wrapper (без сдвига камеры). */
export function projectSvgPointInWorld(
  svgX: number,
  svgY: number,
  layout: LevelCanvasLayout,
): PlayerScreenPosition {
  return {
    leftPx: svgX * layout.displayScale,
    topPx: svgY * layout.displayScale,
  };
}
